import {html, nothing} from 'lit';
import {FlywheelComponent} from '@gl/lit/lib/component.js';
import styles from './style.scss';
import {customElement, state, property} from 'lit/decorators.js';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import type {TagSlim} from '@gl/lit/types/audiences.js';
import type {Nullable} from '@gl/lit/types/general.js';

export const AUDIENCE_MENU_ELEMENT_TAG = 'audience-menu';

const INITIAL_NUMBER_OF_TAGS_TO_DISPLAY = 15;
/**
 * AudienceMenu -- Audience Menu, showing audience folders for tags if any
 */
@customElement(AUDIENCE_MENU_ELEMENT_TAG)
export default class AudienceMenu extends FlywheelComponent {
  @property({type: Boolean})
  active = false;

  @property({type: Number})
  tag: Nullable<number> = null;

  @property({type: String})
  path = '';

  @property({type: Array})
  tags: TagSlim[] = [];

  @state()
  open = false;

  @state()
  showFullList = false;

  static get styles() {
    return [styles];
  }

  load() {
    this.open = !!this.tag;
  }

  renderFolders() {
    const size = this.tags.length;
    const limit = this.showFullList ? size : INITIAL_NUMBER_OF_TAGS_TO_DISPLAY;
    const visibleTags = this.tags.slice(0, limit);
    const remainderTags = limit < size ? this.tags.slice(limit, size) : [];

    return html`<div class="audiences-tags-panel">
      ${repeat(
        visibleTags,
        (_, idx: number) => `repeatkey:${AUDIENCE_MENU_ELEMENT_TAG}-${idx}`,
        (tag: TagSlim) => {
          return html`
            <div class="audiences-tags-panel__link-container">
              <a
                href="${this.path}?tag=${tag.id}"
                class="audiences-tags-panel__link-container__link"
              >
                <fly-tooltip>
                  <span
                    class="audience-exports__tooltip--content"
                    slot="content"
                  >
                    ${tag.name}
                  </span>
                  <div
                    class="audiences-tags-panel__link-container__link__text${this
                      .tag === tag.id
                      ? '__selected'
                      : ''}"
                  >
                    # ${tag.name}
                  </div>
                </fly-tooltip>
              </a>
            </div>
          `;
        }
      )}
      ${when(
        remainderTags.length,
        () => this.renderRenderToggle('Show more', 'Show all tags'),
        () =>
          this.renderRenderToggle(
            'Show less',
            'Show only the most relevant tags'
          )
      )}
    </div>`;
  }

  renderRenderToggle(text: string, tooltip: string) {
    if (this.tags.length <= INITIAL_NUMBER_OF_TAGS_TO_DISPLAY) return '';

    return html`<div class="audiences-tags-panel__link-container">
      <a
        href=""
        class="audiences-tags-panel__link-container__link"
        @click=${(e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          this.showFullList = !this.showFullList;
          this.requestUpdate();
        }}
      >
        <fly-tooltip>
          <span class="audience-exports__tooltip--content" slot="content">
            ${tooltip}
          </span>
          <div class="audiences-tags-panel__link-container__link__toggle">
            ${text}
          </div>
        </fly-tooltip>
      </a>
    </div>`;
  }

  render() {
    return html`
      <a href=${this.path} class="aside-menu-link">
        <fly-list-item ?selected=${this.active} hasIcon large>
          <div slot="icon">
            <fly-icon icon="dashboard"></fly-icon>
          </div>
          <div class="aside-menu-link__caption">
            Audiences
            ${when(
              this.tags.length,
              () => html`
                <fly-icon
                  icon=${this.open ? 'expand_more' : 'chevron_right'}
                  class="align-right"
                  @click=${(e: MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.open = !this.open;
                    this.requestUpdate();
                  }}
                >
                </fly-icon>
              `,
              () => nothing
            )}
          </div>
          ${when(
            this.tags.length && this.open,
            () => this.renderFolders(),
            () => nothing
          )}
        </fly-list-item>
      </a>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [AUDIENCE_MENU_ELEMENT_TAG]: AudienceMenu;
  }
}
